import { useCallback, useEffect, useState } from 'react'
import { useSettings } from '@/contexts/SettingsContext'
import { createSelectionKey, parseSelectionKey } from '../ContentManagerConstants'
import type { SectionId } from '@/components/content-manager/ContentSidebar'
import type { ContentType } from '@/types/Content'

/**
 * Share target information for playbook/document sharing dialogs
 */
export interface ShareTarget {
	id: string
	name: string
	teamId: string | null
}

/**
 * Rename target information for rename dialog
 */
export interface RenameTarget {
	id: string
	name: string
	type: 'playbook' | 'folder' | 'document'
}

/**
 * Content manager UI state interface
 */
export interface ContentManagerState {
	activeSection: SectionId
	selectedFolderId: string | null
	searchQuery: string
	viewMode: 'grid' | 'list'
	selectedItems: Set<string>
	shareTarget: ShareTarget | null
	renameTarget: RenameTarget | null
}

/**
 * Content manager state actions interface
 */
export interface ContentManagerStateActions {
	setActiveSection: (section: SectionId) => void
	setSelectedFolderId: (folderId: string | null) => void
	setSearchQuery: (query: string) => void
	setViewMode: (mode: 'grid' | 'list') => void
	toggleSelection: (id: string, type: ContentType) => void
	isItemSelected: (id: string, type: ContentType) => boolean
	clearSelection: () => void
	setShareTarget: (target: ShareTarget | null) => void
	setRenameTarget: (target: RenameTarget | null) => void
	selectedCount: number
}

/**
 * Hook for managing ContentManagerPage UI state
 *
 * Consolidates all UI state management into a single hook following
 * the pattern established by usePlaybookEditorState.
 *
 * Features:
 * - Persists active section to settings context
 * - Clears selection when changing sections or folders
 * - Provides type-safe selection management
 * - Consolidates share target state (previously 3 separate variables)
 */
export function useContentManagerState(): ContentManagerState & ContentManagerStateActions {
	const { lastContentSection, setLastContentSection } = useSettings()

	// Core state
	const [activeSection, setActiveSectionInternal] = useState<SectionId>(lastContentSection)
	const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
	const [searchQuery, setSearchQuery] = useState('')
	const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

	// Selection state (format: "type:id")
	const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())

	// Share target state (consolidated from 3 separate variables)
	const [shareTarget, setShareTarget] = useState<ShareTarget | null>(null)

	// Rename target state for rename dialog
	const [renameTarget, setRenameTarget] = useState<RenameTarget | null>(null)

	// Persist active section to settings
	useEffect(() => {
		setLastContentSection(activeSection)
	}, [activeSection, setLastContentSection])

	// Clear selection when changing sections or folders
	useEffect(() => {
		setSelectedItems(new Set())
	}, [activeSection, selectedFolderId])

	// Section change clears folder selection (mutual exclusivity)
	const setActiveSection = useCallback((section: SectionId) => {
		setActiveSectionInternal(section)
		setSelectedFolderId(null)
	}, [])

	// Selection management
	const toggleSelection = useCallback((id: string, type: ContentType) => {
		const key = createSelectionKey(type, id)
		setSelectedItems(prev => {
			const next = new Set(prev)
			if (next.has(key)) {
				next.delete(key)
			} else {
				next.add(key)
			}
			return next
		})
	}, [])

	const isItemSelected = useCallback((id: string, type: ContentType): boolean => {
		return selectedItems.has(createSelectionKey(type, id))
	}, [selectedItems])

	const clearSelection = useCallback(() => {
		setSelectedItems(new Set())
	}, [])

	return {
		// State
		activeSection,
		selectedFolderId,
		searchQuery,
		viewMode,
		selectedItems,
		shareTarget,
		renameTarget,

		// Actions
		setActiveSection,
		setSelectedFolderId,
		setSearchQuery,
		setViewMode,
		toggleSelection,
		isItemSelected,
		clearSelection,
		setShareTarget,
		setRenameTarget,
		selectedCount: selectedItems.size,
	}
}

/**
 * Utility to parse selected items back to typed objects
 */
export function parseSelectedItems(
	selectedItems: Set<string>
): Array<{ type: ContentType; id: string }> {
	return Array.from(selectedItems)
		.map(key => {
			const parsed = parseSelectionKey(key)
			if (!parsed) return null
			return { type: parsed.type as ContentType, id: parsed.id }
		})
		.filter((item): item is { type: ContentType; id: string } => item !== null)
}
